import React from 'react';

import { usStateNames } from '@hero/hero-utils/address/states';
import useIsMinBreakpoint from '@hero/react-hooks/mediaQuery/useIsMinBreakpoint';
import Button from '@hero/ui-kit/inputs/Button';
import Combobox from '@hero/ui-kit/inputs/Combobox';
import { useFormContext } from '@hero/ui-kit/inputs/Form';
import Input from '@hero/ui-kit/inputs/Input';
import InputError from '@hero/ui-kit/inputs/InputError';
import Container from '@hero/ui-kit/layout/Container';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import AddressCombobox from '../../../../components/AddressGoogleCombobox';
import ExistingHeroUserFormFields from '../ExistingHeroUserFormFields';
import RadioButton from '../RadioButton';
import VerifiedUser from '../VerifiedUser';

const FormElements: React.FC<{
    error?: string;
    loading: boolean;
    showAdminEmailError?: boolean;
    showHeroSerialErrorError?: boolean;
    adminEmail?: string | null;
    externalSerial?: string | null;
    isExistingMember?: boolean;
    formVersion?: string | null;
    allowVerifiedExistingMemberLock?: boolean;
}> = ({
    loading,
    showAdminEmailError = false,
    showHeroSerialErrorError = false,
    isExistingMember = false,
    adminEmail,
    externalSerial,
    formVersion,
    allowVerifiedExistingMemberLock = true
}) => {
    const {
        watch,
        setValue,
        formState: { errors },
        clearErrors
    } = useFormContext();
    const isMinL = useIsMinBreakpoint('l');
    const isMember = watch('is_member');

    const isElectionMember = ['backoffice', 'mobile'].includes(formVersion || '');

    const setIsMember = React.useCallback(
        (value: boolean) => {
            setValue('is_member', value);
            clearErrors('is_member');
        },
        [setValue]
    );

    return (
        <>
            <Section noDefaultPadding marginVertical="regular">
                <P strong size="large" noDefaultMargin>
                    Details
                </P>

                <Section noDefaultPadding marginVertical="small">
                    <Container
                        gridTemplateColumns={isMinL ? '1fr 1fr' : '1fr'}
                        gridColumnGap="regular"
                    >
                        <Input autoFocus name="first_name" displayName="First name" />
                        <Input name="last_name" displayName="Last name" />
                    </Container>
                    <Input name="email" displayName="Primary Patient Email" />
                </Section>
            </Section>

            <Section noDefaultPadding marginVertical="regular">
                <P strong size="large" noDefaultMargin>
                    Org info
                </P>

                <P noDefaultMargin size="small">
                    Internal patient record. If you have a patient ID from your EMR you may enter it
                    below.
                </P>

                <Section noDefaultPadding marginVertical="small">
                    <Input name="external_user_id" displayName="Patient ID (optional)" />
                </Section>
            </Section>

            {allowVerifiedExistingMemberLock &&
            isExistingMember &&
            adminEmail &&
            externalSerial &&
            isElectionMember ? (
                <Section noDefaultPadding marginVertical="regular">
                    <VerifiedUser adminEmail={adminEmail} serialNumber={externalSerial} />
                </Section>
            ) : (
                <>
                    <Section noDefaultPadding marginVertical="regular">
                        <Container
                            gridTemplateColumns={isMinL ? '1fr 1fr' : '1fr'}
                            gridColumnGap="regular"
                        >
                            <RadioButton
                                checked={isMember === true}
                                onClick={() => setIsMember(true)}
                                title="Existing Hero user"
                                subtitle="Applicant already has Hero device."
                                error={!!errors?.is_member}
                            />
                            <RadioButton
                                checked={isMember === false}
                                onClick={() => setIsMember(false)}
                                title="Order new Hero device"
                                subtitle="New Hero device will be shipped."
                                error={!!errors?.is_member}
                            />
                        </Container>

                        {errors.is_member && (
                            <InputError id={'is_member'}>
                                {errors.is_member.message as string}
                            </InputError>
                        )}
                    </Section>

                    {isMember ? (
                        <ExistingHeroUserFormFields
                            showAdminEmailError={showAdminEmailError}
                            showHeroSerialErrorError={showHeroSerialErrorError}
                        />
                    ) : null}
                </>
            )}

            <Section noDefaultPadding marginVertical="regular">
                <Section noDefaultPadding marginVertical="small">
                    <P strong size="large" noDefaultMargin>
                        {isMember === false ? 'Shipping address' : 'Applicant address'}
                    </P>
                </Section>

                <AddressCombobox zipName="zip_code" />
                <Container gridTemplateColumns={isMinL ? '1fr 1fr' : '1fr'} gridColumnGap="regular">
                    <Input name="address_line_2" displayName="Apt, suite, etc (optional)" />
                    <Input name="city" displayName="City" />
                </Container>

                <Container
                    gridTemplateColumns={isMinL ? '1fr 1fr 2fr' : '1fr'}
                    gridColumnGap="regular"
                >
                    <div style={{ width: '100%' }}>
                        <Combobox
                            name="state"
                            displayName="State"
                            suggestions={usStateNames}
                            noExtraMargin
                            onlyValidateWithError={true}
                        />
                    </div>
                    <Input name="zip_code" displayName="Zip" />
                    <Input name="phone" displayName="Phone" styles={{ marginBottom: '0.5rem' }} />
                </Container>
            </Section>

            <Section noDefaultPadding marginVertical="regular">
                <Section centered noDefaultPadding>
                    <Button type="submit" disabled={loading} width={isMinL ? 'large' : 'full'}>
                        Submit
                    </Button>
                </Section>
            </Section>
        </>
    );
};

export default FormElements;
